import { gql } from '@apollo/client';

export const GET_USER_CONTACT_INFO_GQL = gql`
  query GetUserContactInfo($userId: uuid!) {
    user(where: { id: { _eq: $userId } }) {
      phone_number
      email
    }
  }
`;

export const GET_USER_NOTIFICATION_TRANSPORT_SETTINGS_GQL = gql`
  query GetUserNotificationTransportSettings($userId: uuid!) {
    user_notification_transport_settings(where: { user_id: { _eq: $userId } }) {
      user_id
      sms
      push
      email
    }
  }
`;

export const UPSERT_USER_NOTIFICATION_TRANSPORT_SETTINGS_GQL = gql`
  mutation UpsertUserNotificationTransportSettings($user_id: uuid!, $sms: Boolean!, $push: Boolean!, $email: Boolean!) {
    insert_user_notification_transport_settings(
      objects: { user_id: $user_id, sms: $sms, push: $push, email: $email }
      on_conflict: { constraint: user_notification_transport_settings_pkey, update_columns: [sms, push, email] }
    ) {
      affected_rows
    }
  }
`;

export const SUBSCRIBE_EMAIL_NOTIFICATION_STATUS = gql`
  subscription SubscribeEmailNotificationStatus($messageId: uuid!) {
    email_notification_status(
      where: { message: { id: { _eq: $messageId } } }
      order_by: { occurred_at: desc_nulls_last }
    ) {
      id
      occurred_at
      status
      message {
        id
        sent_to
        sent_at
      }
    }
  }
`;

export const SUBSCRIBE_SMS_NOTIFICATION_STATUS = gql`
  subscription SubscribeSmsNotificationStatus($messageId: uuid!) {
    sms_notification_status(
      where: { message: { id: { _eq: $messageId } } }
      order_by: { occurred_at: desc_nulls_last }
    ) {
      id
      occurred_at
      status
      message {
        id
        sent_to
        sent_at
      }
    }
  }
`;

export const SUBSCRIBE_PUSH_NOTIFICATION_STATUS = gql`
  subscription SubscribePushNotificationStatus($messageId: uuid!) {
    push_notification_receipt(
      where: { message: { id: { _eq: $messageId } } }
      order_by: { receipt_checked_at: desc_nulls_last }
    ) {
      id
      occurred_at: receipt_checked_at
      status: receipt_status
      message {
        id
        sent_to
        sent_at
      }
      token {
        phone_info
      }
    }
  }
`;

export const GET_USER_TIER_ASSIGNMENTS_GQL = gql`
  query GetUserTierAssignments($userId: uuid!) {
    user_organization(where: { user_id: { _eq: $userId }, deleted_at: { _is_null: true } }) {
      organization {
        id
        name
        selected_notification_plan {
          plan {
            id
            name
          }
        }
      }
      user_organization_notification_tier_assignment {
        id
        tier_id
        tier {
          id
          name
          public_description
        }
      }
    }
    user_barn(where: { user_id: { _eq: $userId } }) {
      barn {
        id
        name
        organization {
          id
          name
        }
      }
      user_barn_notification_tier_assignment {
        id
        tier_id
        tier {
          id
          name
          public_description
        }
      }
    }
    user_barn_tier_assignments: user_barn_notification_tier_assignment(
      where: { user_id: { _eq: $userId } }
    ) {
      id
      tier_id
      tier {
        id
        name
        public_description
      }
      barn {
        id
        name
        organization {
          id
          name
        }
      }
    }
  }
`;

export const GET_MOST_RECENT_NOTIFICATION_STATUSES_GQL = gql`
  query GetMostRecentNotificationStatuses($userId: uuid!) {
    sms_statuses: message(
      where: { transported_as: { _eq: "sms" }, user_id: { _eq: $userId } }
      limit: 1
      order_by: { sent_at: desc_nulls_last }
    ) {
      id
      sms_notification_statuses(limit: 20, order_by: { occurred_at: desc_nulls_last }) {
        id
        occurred_at
        status
      }
    }
    email_statuses: message(
      where: { transported_as: { _eq: "email" }, user_id: { _eq: $userId } }
      limit: 1
      order_by: { sent_at: desc_nulls_last }
    ) {
      id
      email_notification_statuses(limit: 20, order_by: { occurred_at: desc_nulls_last }) {
        id
        occurred_at
        status
      }
    }
    push_statuses: message(
      where: { transported_as: { _eq: "push" }, user_id: { _eq: $userId } }
      limit: 1
      order_by: { sent_at: desc_nulls_last }
    ) {
      id
      push_notification_receipts(limit: 20, order_by: { receipt_checked_at: desc_nulls_last }) {
        id
        occurred_at: receipt_checked_at
        status: receipt_status
        token {
          phone_info
        }
      }
    }
  }
`;

export const TEST_TRANSPORT_GQL = gql`
  mutation TestTransport($transportType: String!, $userId: String!) {
    user_notification_transport_type_test(transportType: $transportType, userId: $userId) {
      success
      messageId
      data
    }
  }
`;

export const GET_ORG_TIER_SETTINGS_GQL = gql`
  query GetOrgTierSettings($org_id: uuid!, $tier_id: uuid!) {
    organization_fault_notification_tier_settings_view(
      where: { organization_id: { _eq: $org_id }, tier: { id: { _eq: $tier_id } } }
    ) {
      code
      enabled
      alert_threshold_type
      alert_threshold_seconds
      renotify_enabled
      renotify_threshold_seconds
      notify_on_resolved
    }
  }
`;

export const GET_ORG_TIER_SETTINGS_ALL_GQL = gql`
  query GetOrgTierSettingsAll($org_id: uuid!) {
    organization_fault_notification_tier_settings_view(
      where: { organization_id: { _eq: $org_id } }
    ) {
      tier_id
      code
      enabled
      alert_threshold_type
      alert_threshold_seconds
      renotify_enabled
      renotify_threshold_seconds
      notify_on_resolved
      tier {
        id
        name
      }
    }
  }
`;

export const GET_BARN_TIER_SETTINGS_GQL = gql`
  query GetBarnTierSettings($barn_ids: [uuid!]!) {
    barn_fault_notification_tier_settings_view(where: { barn_id: { _in: $barn_ids } }) {
      barn_id
      tier_id
      code
      enabled
      alert_threshold_type
      alert_threshold_seconds
      renotify_enabled
      renotify_threshold_seconds
      notify_on_resolved
      tier {
        id
        name
      }
    }
  }
`;
